/* import __COLOCATED_TEMPLATE__ from './dropdown-actions.hbs'; */
/* RESPONSIBLE TEAM: team-knowledge-foundations */
import type AppService from 'embercom/services/app-service';
import Component from '@glimmer/component';
import { action } from '@ember/object';
import { AiContentState } from 'embercom/lib/ai-content-library/constants';
import { EntityType } from 'embercom/models/data/entity-types';
import type SyncSourceWrapper from 'embercom/models/knowledge-hub/sync-source-wrapper';
import { inject as service } from '@ember/service';
import type IntlService from 'ember-intl/services/intl';
import type KnowledgeHubService from 'embercom/services/knowledge-hub-service';
import { taskFor } from 'ember-concurrency-ts';
import {
  ActionName,
  actionParameters,
} from 'embercom/components/knowledge-hub/filterable-list/bulk-actions/bulk-action-helper';
import { ContentSourceType } from './content';
import { tracked } from '@glimmer/tracking';
import type ConversationExtractionService from 'embercom/services/conversation-extraction-service';
import type ContentImportLogsModalService from 'embercom/services/content-import-logs-modal-service';
import { SyncBehavior } from 'embercom/models/knowledge-hub/sync-source-wrapper';
import type Folder from 'embercom/models/content-service/folder';
import type Store from '@ember-data/store';
import type RouterService from '@ember/routing/router-service';
import type IntercomConfirmService from 'embercom/services/intercom-confirm-service';

export interface DropdownActionsArgs {
  contentSourceType: ContentSourceType;
  syncSource?: SyncSourceWrapper;
  disabledCriteria?: {
    disableBulkEnable: boolean;
    disableBulkEnableCopilot: boolean;
    disableResync?: boolean;
    disableRouteToFolder?: boolean;
    disableRemoveSource?: boolean;
  };
  showModal?: (syncSourceType: string, entityType: EntityType) => Promise<void>;
  showManageConversationsDrawer?: () => void;
}

export interface DropdownActionsSignature {
  Args: DropdownActionsArgs;
  Blocks: {
    name: [];
    statusLabel: [];
    dropdownActions: [];
  };
}

type SourceRowGroupList = {
  items: {
    text: string;
    onSelectItem?: () => void;
    icon?: string;
    isDisabled?: boolean;
    isDestructive?: boolean;
    tooltipText?: string;
  }[];
};

export default class DropdownActions extends Component<DropdownActionsSignature> {
  @service declare appService: AppService;
  @service declare intl: IntlService;
  @service declare knowledgeHubService: KnowledgeHubService;
  @service declare conversationExtractionService: ConversationExtractionService;
  @service declare notificationsService: any;
  @service declare permissionsService: any;
  @service declare intercomEventService: any;
  @service declare intercomConfirmService: IntercomConfirmService;
  @service declare store: Store;
  @service declare router: RouterService;
  @service declare contentImportLogsModalService: ContentImportLogsModalService;
  @service declare modalService: any;
  @tracked showDisableConversationExtractionModal = false;
  @tracked showZendeskMigrationModal = false;
  @tracked zendeskMigrationModalContext: any;

  get app() {
    return this.appService.app;
  }

  get conversationExtractionSettings() {
    return this.conversationExtractionService.conversationExtractionSettings;
  }

  @action async applyBulkEnable(entityType: EntityType, actionName: ActionName, folderId?: number) {
    let { requiredPermission } = actionParameters[actionName];
    try {
      await this.permissionsService.checkPermission(requiredPermission);
    } catch (e) {
      return;
    }

    let folderIds: string[] | undefined;

    if (folderId) {
      folderIds = [folderId.toString()];

      if (entityType === EntityType.InternalArticle) {
        let folder = this.store.peekRecord('content-service/folder', folderId) as Folder;

        folderIds = folder.descendantIds.concat(folderId.toString());
      }
    }

    await taskFor(this.knowledgeHubService.requestBulkEnableTask).perform(
      entityType,
      actionName,
      folderIds,
    );
  }

  private async confirmRemoveModal(title: string, body: string): Promise<boolean> {
    return await this.intercomConfirmService.confirm({
      title,
      body,
      primaryButtonType: 'primary-destructive',
      confirmButtonText: this.intl.t('fin-ai-agent.knowledge.actions.remove'),
    });
  }

  @action getGroupList(contentSourceType: ContentSourceType): SourceRowGroupList[] {
    switch (contentSourceType) {
      case ContentSourceType.PublicArticle:
        return this.getArticleGroupList();
      case ContentSourceType.Website:
        return this.getWebsiteGroupList();
      case ContentSourceType.Snippet:
        return this.getSnippetGroupList();
      case ContentSourceType.PDF:
        return this.getPDFGroupList();
      case ContentSourceType.ZendeskArticle:
        return this.getZendeskArticleGroupList();
      case ContentSourceType.ConversationExtraction:
        return this.getConversationExtractionGroupList();
      case ContentSourceType.CustomAnswer:
        return this.getCustomAnswersGroupList();
      case ContentSourceType.InternalArticle:
        return this.getInternalArticleGroupList();
      case ContentSourceType.ConversationExcerpt:
        return this.getConversationExcerptGroupList();
      case ContentSourceType.SalesforceKnowledge:
        return this.getSalesforceKnowledgeGroupList();
      case ContentSourceType.FreshdeskKnowledge:
        return this.getDocumentContentGroupList();
      case ContentSourceType.Document360:
        return this.getDocumentContentGroupList();
      case ContentSourceType.Box:
        return this.getDocumentContentGroupList();
      default:
        return [];
    }
  }

  private getArticleGroupList(): SourceRowGroupList[] {
    return [
      {
        items: [
          {
            text: this.bulkActionButtonText('articles', 'enable'),
            onSelectItem: () => {
              this.applyBulkEnable(EntityType.ArticleContent, ActionName.AIAgentEnable);
              this.instrumentDropdownAction('enable-all', 'public-article');
            },
            icon: 'check',
            isDisabled: this.args.disabledCriteria?.disableBulkEnable,
          },
          // hide copilot actions if standalone platform doesn't support it
          ...(!this.app.canUseStandalone || this.app.canUseCopilotOnStandalone
            ? [
                {
                  text: this.bulkActionButtonText('articles', 'enable-copilot'),
                  onSelectItem: () => {
                    this.applyBulkEnable(EntityType.ArticleContent, ActionName.AICopilotEnable);
                    this.instrumentDropdownAction('enable-all', 'public-article');
                  },
                  icon: 'check',
                  isDisabled: this.args.disabledCriteria?.disableBulkEnableCopilot,
                },
              ]
            : []),
          {
            text: this.intl.t('fin-ai-agent.knowledge.actions.articles.view-all', {
              isStandaloneApp: this.app.isStandaloneApp,
            }),
            onSelectItem: () => {
              this.knowledgeHubService.goToContent(EntityType.ArticleContent, {}, true);
              this.instrumentDropdownAction('view-all', 'public-article');
            },
            icon: 'campaign',
          },
        ],
      },
    ];
  }

  private getWebsiteGroupList(): SourceRowGroupList[] {
    let entity_type =
      this.args.syncSource?.entityType === EntityType.ExternalContent ? 'websites' : 'documents';
    let defaultItems = {
      items: [
        {
          text: this.intl.t(`fin-ai-agent.knowledge.actions.${entity_type}.resync`),
          onSelectItem: () => {
            if (this.args.syncSource?.resync) {
              taskFor(this.args.syncSource.resync).perform();
            }
            this.instrumentDropdownAction('resync', 'website');
          },
          icon: 'sync',
          isDisabled:
            this.args.syncSource?.isSyncing ||
            this.args.syncSource?.syncBehavior === SyncBehavior.API,
        },
        ...(this.appService.app.canUseFeature('team-knowledge-foundations-new-sync-summary') &&
        this.args.syncSource?.sourceType === 'external_content' &&
        this.args.syncSource !== undefined
          ? [
              {
                text: this.intl.t('fin-ai-agent.knowledge.actions.websites.view-sync-history'),
                onSelectItem: () => {
                  if (this.args.syncSource) {
                    this.contentImportLogsModalService.openContentImportLogsSheet(
                      this.args.syncSource,
                    );
                    this.instrumentDropdownAction('view-sync-history', 'website');
                  }
                },
                icon: 'history',
              },
            ]
          : []),
        {
          text: this.intl.t(`fin-ai-agent.knowledge.actions.${entity_type}.remove`),
          onSelectItem: async () => {
            let confirmed = await this.confirmRemoveModal(
              this.intl.t(`fin-ai-agent.knowledge.actions.${entity_type}.remove-title`),
              this.intl.t(`fin-ai-agent.knowledge.actions.${entity_type}.confirm-remove`),
            );
            if (!confirmed) {
              return;
            }

            this.args.syncSource?.remove();
            this.instrumentDropdownAction('remove', 'website');
          },
          isDestructive: true,
          icon: 'trash',
        },
      ],
    };

    if (this.args.syncSource?.hasError) {
      return [defaultItems];
    }

    if (this.args.syncSource?.status === 'syncing') {
      return [
        {
          items: [
            {
              text: this.intl.t(`fin-ai-agent.knowledge.actions.${entity_type}.view-all`, {
                isStandaloneApp: this.app.isStandaloneApp,
              }),
              onSelectItem: () => {
                if (!this.args.syncSource) {
                  return;
                }

                let withEntityType = this.args.syncSource.entityType !== EntityType.ExternalContent;
                this.knowledgeHubService.goToSyncSourceFolder(
                  this.args.syncSource,
                  {
                    chatbotState: AiContentState.USED_BY_FIN,
                  },
                  true,
                  withEntityType,
                );
                this.instrumentDropdownAction('view-all', 'website');
              },
              icon: 'campaign',
              isDisabled:
                this.args.disabledCriteria?.disableRouteToFolder || !this.args.syncSource.folderId,
            },
            {
              text: this.intl.t(`fin-ai-agent.knowledge.actions.${entity_type}.remove`),
              onSelectItem: async () => {
                let confirmed = await this.confirmRemoveModal(
                  this.intl.t(`fin-ai-agent.knowledge.actions.${entity_type}.remove-title`),
                  this.intl.t(`fin-ai-agent.knowledge.actions.${entity_type}.confirm-remove`),
                );
                if (!confirmed) {
                  return;
                }

                this.args.syncSource?.remove();
                this.instrumentDropdownAction('remove', 'website');
              },
              isDestructive: true,
              icon: 'trash',
            },
          ],
        },
      ];
    }

    return [
      {
        items: [
          {
            text: this.bulkActionButtonText(entity_type, 'enable'),
            onSelectItem: () => {
              this.applyBulkEnable(
                EntityType.ExternalContent,
                ActionName.AIAgentEnable,
                this.args.syncSource?.folderId,
              );
              this.instrumentDropdownAction('enable-all', 'website');
            },
            icon: 'check',
            isDisabled: this.args.disabledCriteria?.disableBulkEnable,
          },
          // hide copilot actions if standalone platform doesn't support it
          ...(!this.app.canUseStandalone || this.app.canUseCopilotOnStandalone
            ? [
                {
                  text: this.bulkActionButtonText(entity_type, 'enable-copilot'),
                  onSelectItem: () => {
                    this.applyBulkEnable(
                      EntityType.ExternalContent,
                      ActionName.AICopilotEnable,
                      this.args.syncSource?.folderId,
                    );
                    this.instrumentDropdownAction('enable-all', 'website');
                  },
                  icon: 'check',
                  isDisabled: this.args.disabledCriteria?.disableBulkEnableCopilot,
                },
              ]
            : []),
          {
            text: this.intl.t(`fin-ai-agent.knowledge.actions.${entity_type}.view-all`, {
              isStandaloneApp: this.app.isStandaloneApp,
            }),
            onSelectItem: () => {
              if (!this.args.syncSource) {
                return;
              }

              let withEntityType = this.args.syncSource.entityType !== EntityType.ExternalContent;
              this.knowledgeHubService.goToSyncSourceFolder(
                this.args.syncSource,
                {
                  chatbotState: AiContentState.USED_BY_FIN,
                },
                true,
                withEntityType,
              );
              this.instrumentDropdownAction('view-all', 'website');
            },
            icon: 'campaign',
            isDisabled:
              this.args.disabledCriteria?.disableRouteToFolder || !this.args.syncSource?.folderId,
          },
          ...defaultItems.items,
        ],
      },
    ];
  }

  private getSalesforceKnowledgeGroupList(): SourceRowGroupList[] {
    let entity_type = 'salesforce-knowledge';
    let defaultItems = {
      items: [
        {
          text: this.intl.t(`fin-ai-agent.knowledge.actions.${entity_type}.resync`),
          onSelectItem: () => {
            if (this.args.syncSource?.resync) {
              taskFor(this.args.syncSource.resync).perform();
            }
            this.instrumentDropdownAction('resync', 'website');
          },
          icon: 'sync',
          isDisabled:
            this.args.syncSource?.isSyncing ||
            this.args.syncSource?.syncBehavior === SyncBehavior.API,
        },
        {
          text: this.intl.t(`fin-ai-agent.knowledge.actions.${entity_type}.remove`),
          onSelectItem: async () => {
            let confirmed = await this.confirmRemoveModal(
              this.intl.t(`fin-ai-agent.knowledge.actions.${entity_type}.remove-title`),
              this.intl.t(`fin-ai-agent.knowledge.actions.${entity_type}.confirm-remove`),
            );
            if (!confirmed) {
              return;
            }

            this.args.syncSource?.remove();
            this.instrumentDropdownAction('remove', 'website');
          },
          isDestructive: true,
          icon: 'trash',
        },
      ],
    };

    if (this.args.syncSource?.hasError) {
      return [
        {
          items: [
            ...defaultItems.items.slice(0, 1),
            {
              text: this.intl.t(`fin-ai-agent.knowledge.actions.${entity_type}.manage-sync`),
              onSelectItem: () => {
                this.router.transitionTo(this.router.currentRouteName, {
                  queryParams: {
                    openSalesforceSync: true,
                    salesforceSyncStep: 1,
                    salesforceSyncSourceId: this.args.syncSource?.sourceId,
                  },
                });
              },
              icon: 'settings',
            },
            ...defaultItems.items.slice(1),
          ],
        },
      ];
    }

    if (this.args.syncSource?.status === 'syncing') {
      return [
        {
          items: [
            {
              text: this.intl.t(`fin-ai-agent.knowledge.actions.${entity_type}.view-all`, {
                isStandaloneApp: this.app.isStandaloneApp,
              }),
              onSelectItem: () => {
                if (!this.args.syncSource) {
                  return;
                }

                let withEntityType = this.args.syncSource.entityType !== EntityType.ExternalContent;
                this.knowledgeHubService.goToSyncSourceFolder(
                  this.args.syncSource,
                  {
                    chatbotState: AiContentState.USED_BY_FIN,
                  },
                  true,
                  withEntityType,
                );
                this.instrumentDropdownAction('view-all', 'website');
              },
              icon: 'campaign',
              isDisabled:
                this.args.disabledCriteria?.disableRouteToFolder || !this.args.syncSource.folderId,
            },
            {
              text: this.intl.t(`fin-ai-agent.knowledge.actions.${entity_type}.remove`),
              onSelectItem: async () => {
                let confirmed = await this.confirmRemoveModal(
                  this.intl.t(`fin-ai-agent.knowledge.actions.${entity_type}.remove-title`),
                  this.intl.t(`fin-ai-agent.knowledge.actions.${entity_type}.confirm-remove`),
                );
                if (!confirmed) {
                  return;
                }

                this.args.syncSource?.remove();
                this.instrumentDropdownAction('remove', 'website');
              },
              isDestructive: true,
              icon: 'trash',
            },
          ],
        },
      ];
    }

    return [
      {
        items: [
          {
            text: this.intl.t(`fin-ai-agent.knowledge.actions.${entity_type}.enable`),
            onSelectItem: () => {
              this.applyBulkEnable(
                EntityType.FileSourceContent,
                ActionName.AIAgentEnable,
                this.args.syncSource?.folderId,
              );
              this.instrumentDropdownAction('enable-all', 'website');
            },
            icon: 'check',
            isDisabled: this.args.disabledCriteria?.disableBulkEnable,
          },
          {
            text: this.intl.t(`fin-ai-agent.knowledge.actions.${entity_type}.view-all`),
            onSelectItem: () => {
              if (!this.args.syncSource) {
                return;
              }

              let withEntityType = this.args.syncSource.entityType !== EntityType.ExternalContent;
              this.knowledgeHubService.goToSyncSourceFolder(
                this.args.syncSource,
                {
                  chatbotState: AiContentState.USED_BY_FIN,
                },
                true,
                withEntityType,
              );
              this.instrumentDropdownAction('view-all', 'website');
            },
            icon: 'campaign',
            isDisabled:
              this.args.disabledCriteria?.disableRouteToFolder || !this.args.syncSource?.folderId,
          },
          ...defaultItems.items.slice(0, 1),
          {
            text: this.intl.t(`fin-ai-agent.knowledge.actions.${entity_type}.manage-sync`),
            onSelectItem: () => {
              this.router.transitionTo(this.router.currentRouteName, {
                queryParams: {
                  openSalesforceSync: true,
                  salesforceSyncStep: 1,
                  salesforceSyncSourceId: this.args.syncSource?.sourceId,
                },
              });
            },
            icon: 'settings',
          },
          ...defaultItems.items.slice(1),
        ],
      },
    ];
  }

  private getSnippetGroupList(): SourceRowGroupList[] {
    return [
      {
        items: [
          {
            text: this.bulkActionButtonText('snippets', 'enable'),
            onSelectItem: () => {
              this.applyBulkEnable(EntityType.ContentSnippet, ActionName.AIAgentEnable);
              this.instrumentDropdownAction('enable-all', 'snippet');
            },
            icon: 'check',
            isDisabled: this.args.disabledCriteria?.disableBulkEnable,
          },
          // hide copilot actions if standalone platform doesn't support it
          ...(!this.app.canUseStandalone || this.app.canUseCopilotOnStandalone
            ? [
                {
                  text: this.bulkActionButtonText('snippets', 'enable-copilot'),
                  onSelectItem: () => {
                    this.applyBulkEnable(EntityType.ContentSnippet, ActionName.AICopilotEnable);
                    this.instrumentDropdownAction('enable-all', 'snippet');
                  },
                  icon: 'check',
                  isDisabled: this.args.disabledCriteria?.disableBulkEnableCopilot,
                },
              ]
            : []),
          {
            text: this.intl.t('fin-ai-agent.knowledge.actions.snippets.view-all', {
              isStandaloneApp: this.app.isStandaloneApp,
            }),
            onSelectItem: () => {
              this.knowledgeHubService.goToContent(EntityType.ContentSnippet, {}, true);
              this.instrumentDropdownAction('view-all', 'snippet');
            },
            icon: 'campaign',
          },
        ],
      },
    ];
  }

  private getPDFGroupList(): SourceRowGroupList[] {
    return [
      {
        items: [
          {
            text: this.bulkActionButtonText('pdfs', 'enable'),
            onSelectItem: () => {
              this.applyBulkEnable(EntityType.FileSourceContent, ActionName.AIAgentEnable);
              this.instrumentDropdownAction('enable-all', 'pdf');
            },
            icon: 'check',
            isDisabled: this.args.disabledCriteria?.disableBulkEnable,
          },
          // hide copilot actions if standalone platform doesn't support it
          ...(!this.app.canUseStandalone || this.app.canUseCopilotOnStandalone
            ? [
                {
                  text: this.bulkActionButtonText('pdfs', 'enable-copilot'),
                  onSelectItem: () => {
                    this.applyBulkEnable(EntityType.FileSourceContent, ActionName.AICopilotEnable);
                    this.instrumentDropdownAction('enable-all', 'pdf');
                  },
                  icon: 'check',
                  isDisabled: this.args.disabledCriteria?.disableBulkEnableCopilot,
                },
              ]
            : []),
          {
            text: this.intl.t('fin-ai-agent.knowledge.actions.pdfs.view-all', {
              isStandaloneApp: this.app.isStandaloneApp,
            }),
            onSelectItem: () => {
              this.knowledgeHubService.goToContent(EntityType.FileSourceContent, {}, true);
              this.instrumentDropdownAction('view-all', 'pdf');
            },
            icon: 'campaign',
          },
        ],
      },
    ];
  }

  private getZendeskArticleGroupList(): SourceRowGroupList[] {
    let items: SourceRowGroupList['items'] = [
      {
        text: this.intl.t('fin-ai-agent.knowledge.actions.zendesk.resync'),
        onSelectItem: () => {
          if (this.args.syncSource?.resync) {
            taskFor(this.args.syncSource.resync).perform();
          }
          this.instrumentDropdownAction('resync', 'zendesk-article');
        },
        icon: 'sync',
      },
    ];

    if (this.args.syncSource && this.shouldShowMigrateButton(this.args.syncSource)) {
      items.push({
        text: this.intl.t(
          'knowledge-hub.overview.sections.syncs.rows.active.action.options.migrate',
        ),
        icon: 'upgrade',
        onSelectItem: () => {
          if (this.args.syncSource) {
            this.migrateToNewImporter(this.args.syncSource);
            this.instrumentDropdownAction('migrate', 'zendesk-article');
          }
        },
        isDisabled: this.args.syncSource.isSyncing,
        tooltipText: this.args.syncSource.isSyncing
          ? this.intl.t(
              'knowledge-hub.overview.sections.syncs.rows.active.action.options.disabled-migrate-tooltip',
            )
          : undefined,
      });
    }

    items.push({
      text: this.intl.t('fin-ai-agent.knowledge.actions.zendesk.remove', {
        isStandaloneApp: this.app.isStandaloneApp,
      }),
      onSelectItem: () => {
        this.args.syncSource?.remove();
        this.instrumentDropdownAction('remove', 'zendesk-article');
      },
      isDestructive: true,
      icon: 'trash',
    });

    return [
      {
        items,
      },
    ];
  }

  private getConversationExtractionGroupList(): SourceRowGroupList[] {
    let disableUpdateSettings =
      this.conversationExtractionSettings?.isLatestImportInProgress ?? false;
    return [
      {
        items: [
          {
            text: this.intl.t(
              'fin-ai-agent.knowledge.actions.conversation-extraction.update-settings',
            ),
            onSelectItem: async () => {
              await this.args.showModal?.('conversation-extraction', EntityType.ContentSnippet);
              this.instrumentDropdownAction('update-settings', 'conversation-extraction');
            },
            icon: 'settings',
            isDisabled: disableUpdateSettings,
            tooltipText: disableUpdateSettings
              ? this.intl.t(
                  'knowledge-hub.conversational.conversation-extraction-settings.settings-blocked-reason.import-in-progress',
                )
              : undefined,
          },
          {
            text: this.intl.t(
              'fin-ai-agent.knowledge.actions.conversation-extraction.view-content',
              {
                isStandaloneApp: this.app.isStandaloneApp,
              },
            ),
            onSelectItem: async () => {
              await this.conversationExtractionService.goToContent();
              this.instrumentDropdownAction('view-content', 'conversation-extraction');
            },
            icon: 'article',
          },
          ...(!this.app.isStandaloneApp
            ? [
                {
                  text: this.intl.t(
                    'fin-ai-agent.knowledge.actions.conversation-extraction.give-feedback',
                  ),
                  onSelectItem: async () => {
                    this.conversationExtractionService.openFeedbackSurvey();
                    this.instrumentDropdownAction('give-feedback', 'conversation-extraction');
                  },
                  icon: 'survey-filled',
                },
              ]
            : []),
          {
            text: this.intl.t(
              'fin-ai-agent.knowledge.actions.conversation-extraction.disable-feature',
            ),
            onSelectItem: async () => {
              this.openShowDisableConversationExtractionModal();
              this.instrumentDropdownAction('disable-feature-modal', 'conversation-extraction');
            },
            icon: 'trash',
            isDestructive: true,
          },
        ],
      },
    ];
  }

  private getCustomAnswersGroupList(): SourceRowGroupList[] {
    return [
      {
        items: [
          {
            text: this.intl.t('fin-ai-agent.knowledge.actions.custom-answers.view-all'),
            onSelectItem: () => {
              this.knowledgeHubService.goToContent(EntityType.Answer, {}, true);
              this.instrumentDropdownAction('view-all', 'custom_answer');
            },
            icon: 'respond',
          },
        ],
      },
    ];
  }

  private getInternalArticleGroupList(): SourceRowGroupList[] {
    return [
      {
        items: [
          ...(!this.app.canUseStandalone
            ? [
                {
                  text: this.bulkActionButtonText('articles', 'enable'),
                  onSelectItem: () => {
                    this.applyBulkEnable(EntityType.InternalArticle, ActionName.AIAgentEnable);
                    this.instrumentDropdownAction('enable-all', 'public-article');
                  },
                  icon: 'check',
                  isDisabled: this.args.disabledCriteria?.disableBulkEnable,
                },
              ]
            : []),
          // hide copilot actions if standalone platform doesn't support it
          ...(!this.app.canUseStandalone || this.app.canUseCopilotOnStandalone
            ? [
                {
                  text: this.bulkActionButtonText('internal-articles', 'enable-copilot'),
                  onSelectItem: () => {
                    this.applyBulkEnable(
                      EntityType.InternalArticle,
                      ActionName.AICopilotEnable,
                      this.args.syncSource?.folderId,
                    );
                    this.instrumentDropdownAction('enable-all', 'internal-article');
                  },
                  icon: 'check',
                  isDisabled: this.args.disabledCriteria?.disableBulkEnableCopilot,
                },
              ]
            : []),
          ...(this.app.canUseStandalone
            ? [
                {
                  text: this.intl.t(`fin-ai-agent.knowledge.actions.internal-articles.resync`),
                  onSelectItem: () => {
                    if (this.args.syncSource?.resync) {
                      taskFor(this.args.syncSource.resync).perform();
                    }
                    this.instrumentDropdownAction('resync', 'website');
                  },
                  icon: 'sync',
                  isDisabled:
                    this.args.syncSource?.isSyncing ||
                    this.args.syncSource?.syncBehavior === SyncBehavior.API,
                },
                {
                  text: this.intl.t(`fin-ai-agent.knowledge.actions.internal-articles.remove`),
                  onSelectItem: async () => {
                    let confirmed = await this.confirmRemoveModal(
                      this.intl.t(`fin-ai-agent.knowledge.actions.internal-articles.remove-title`),
                      this.intl.t(
                        `fin-ai-agent.knowledge.actions.internal-articles.confirm-remove`,
                      ),
                    );
                    if (!confirmed) {
                      return;
                    }

                    this.args.syncSource?.remove();
                    this.instrumentDropdownAction('remove', 'internal-article');
                  },
                  isDestructive: true,
                  icon: 'trash',
                },
              ]
            : [
                ...(this.args.syncSource == null
                  ? [
                      {
                        text: this.intl.t('fin-ai-agent.knowledge.actions.articles.view-all', {
                          isStandaloneApp: this.app.isStandaloneApp,
                        }),
                        onSelectItem: () => {
                          this.knowledgeHubService.goToContent(
                            EntityType.InternalArticle,
                            {},
                            true,
                          );
                          this.instrumentDropdownAction('view-all', 'internal-article');
                        },
                        icon: 'campaign',
                      },
                    ]
                  : [
                      {
                        text: this.intl.t(
                          `fin-ai-agent.knowledge.actions.internal-articles.resync`,
                        ),
                        onSelectItem: () => {
                          if (this.args.syncSource?.resync) {
                            taskFor(this.args.syncSource.resync).perform();
                          }
                          this.instrumentDropdownAction('resync', 'website');
                        },
                        icon: 'sync',
                        isDisabled:
                          this.args.syncSource?.isSyncing ||
                          this.args.syncSource?.syncBehavior === SyncBehavior.API,
                      },
                      {
                        text: this.intl.t(
                          `fin-ai-agent.knowledge.actions.internal-articles.remove`,
                        ),
                        onSelectItem: async () => {
                          let confirmed = await this.confirmRemoveModal(
                            this.intl.t(
                              `fin-ai-agent.knowledge.actions.internal-articles.remove-title`,
                            ),
                            this.intl.t(
                              `fin-ai-agent.knowledge.actions.internal-articles.confirm-remove`,
                            ),
                          );
                          if (!confirmed) {
                            return;
                          }

                          this.args.syncSource?.remove();
                          this.instrumentDropdownAction('remove', 'internal-article');
                        },
                        isDestructive: true,
                        icon: 'trash',
                      },
                    ]),
              ]),
        ],
      },
    ];
  }

  private getDocumentContentGroupList(): SourceRowGroupList[] {
    return [
      {
        items: [
          {
            text: this.intl.t(`fin-ai-agent.knowledge.actions.documents.resync`),
            onSelectItem: () => {
              if (this.args.syncSource?.resync) {
                taskFor(this.args.syncSource.resync).perform();
              }
              this.instrumentDropdownAction('resync', 'document-content');
            },
            icon: 'sync',
            isDisabled:
              this.args.syncSource?.isSyncing ||
              this.args.syncSource?.syncBehavior === SyncBehavior.API,
          },
          {
            text: this.intl.t(`fin-ai-agent.knowledge.actions.documents.remove`),
            onSelectItem: async () => {
              let confirmed = await this.confirmRemoveModal(
                this.intl.t(`fin-ai-agent.knowledge.actions.documents.remove-title`),
                this.intl.t(`fin-ai-agent.knowledge.actions.documents.confirm-remove`),
              );
              if (!confirmed) {
                return;
              }
              this.args.syncSource?.remove();
              this.instrumentDropdownAction('remove', 'document-content');
            },
            isDestructive: true,
            icon: 'trash',
          },
        ],
      },
    ];
  }

  private getConversationExcerptGroupList(): SourceRowGroupList[] {
    return [
      {
        items: [
          {
            text: this.intl.t(
              'fin-ai-agent.knowledge.actions.conversation-excerpt.manage-settings',
            ),
            onSelectItem: () => {
              this.args.showManageConversationsDrawer?.();
              this.instrumentDropdownAction('manage-settings', 'conversation-excerpt');
            },
            icon: 'settings',
          },
        ],
      },
    ];
  }

  private bulkActionButtonText(source: string, action: string): string {
    if (this.app.canUseStandalone) {
      return this.intl.t(`fin-ai-agent.knowledge.actions.${source}.${action}-standalone`);
    }
    return this.intl.t(`fin-ai-agent.knowledge.actions.${source}.${action}`);
  }

  @action openShowDisableConversationExtractionModal() {
    this.showDisableConversationExtractionModal = true;
  }

  @action async disableConversationExtraction() {
    await this.conversationExtractionService.disableFeature(() => {
      this.showDisableConversationExtractionModal = false;
    });
  }

  @action instrumentDropdownAction(actionName: string, source: string): void {
    this.intercomEventService.trackAnalyticsEvent({
      tab: 'content',
      action: actionName,
      object: source,
      place: 'fin_ai_agent_knowledge',
      route: 'setup',
    });
  }

  private shouldShowMigrateButton(syncSource: SyncSourceWrapper): boolean {
    if (!this.appService.app.canUseFeature('team-knowledge-foundations-zendesk-migration-banner')) {
      return false;
    }

    let importSource = this.store.peekRecord('import-service/import-source', syncSource.sourceId);
    return importSource == null;
  }

  @action
  async migrateToNewImporter(syncSource: SyncSourceWrapper) {
    try {
      await this.permissionsService.checkPermission('can_manage_knowledge_base_content');
    } catch (error) {
      return;
    }

    try {
      let syncSettings = await this.store.findAll('articles/article-sync-setting');
      let syncSetting = syncSettings.find(
        (setting: any) => setting.id === syncSource.sourceId.toString(),
      );

      if (!syncSetting || !syncSetting.siteUrl) {
        throw new Error('Sync setting not found or missing site URL');
      }

      this.zendeskMigrationModalContext = {
        id: 'zendesk',
        title: this.intl.t('knowledge-hub.sync-modal.zendesk.migration-title'),
        component: 'knowledge-hub/setup-modal/sync-modal/zendesk',
        componentArgs: {
          isMigrating: true,
          existingSyncSetting: syncSetting,
        },
      };

      this.showZendeskMigrationModal = true;
    } catch (error) {
      this.notificationsService.notifyError(
        this.intl.t('knowledge-hub.sync-modal.zendesk.migration-failed'),
      );
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'FinAiAgent::Knowledge::DropdownActions': typeof DropdownActions;
  }
}
